import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { useSimpleDealers } from 'services/Data/DealerServices';
import { useActiveCart, useDeleteCart } from 'services/Data/CartService';
import { useActiveExteriorSignageQuote, useDeleteExteriorSignageQuote } from 'services/Data/ExteriorSignageQuoteService';
import {
  useActiveMerchandisingProgramQuote,
  useDeleteMerchandisingProgramQuote,
} from 'services/Data/MerchandisingProgramQuoteService';
import { LanguageEnumValue } from 'enums/LanguageEnum';
import { brpClubpatchProfile, updateProfile } from 'redux/Account/AccountAction';
import { HOST, IsClub, IsImpersonationMode, localeStorageLanguageKey } from 'config/config';
import useToast from 'services/Hooks/useToast';

const wait = durationMs => new Promise(resolve => setTimeout(resolve, durationMs));

const resolveTemporaryDealerId = profile =>
  profile?.temporaryDealer_Id ?? profile?.temporaryDealerId ?? profile?.TemporaryDealer_Id ?? null;

const isSameDealerId = (left, right) =>
  String(left || '')
    .trim()
    .toLowerCase() ===
  String(right || '')
    .trim()
    .toLowerCase();

const QuickDealerChangeModal = ({ show, onClose, switchMode = 'default', copyPayload = null }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { errorMessage } = useToast();

  const accountReducer = useSelector(state => state.AccountReducer);
  const { data: availableDealers, isLoading: isLoadingDealers } = useSimpleDealers(accountReducer.isLoggedIn, show);
  const { data: activeCart } = useActiveCart(
    accountReducer.isLoggedIn,
    false,
    IsImpersonationMode ? accountReducer?.profile?.temporaryDealer_Id : null,
  );
  const { data: activeExteriorSignageQuote } = useActiveExteriorSignageQuote(accountReducer.isLoggedIn);
  const { data: activeMerchandisingProgramQuote } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);

  const { mutateAsync: deleteCart } = useDeleteCart();
  const { mutateAsync: deleteExteriorSignageQuote } = useDeleteExteriorSignageQuote();
  const { mutateAsync: deleteMerchandisingProgramQuote } = useDeleteMerchandisingProgramQuote();

  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [dealerSearchInput, setDealerSearchInput] = useState('');
  const [initialLanguageCode, setInitialLanguageCode] = useState('en');
  const [hasSavedDealerSelection, setHasSavedDealerSelection] = useState(false);
  const [isSwitchingDealer, setIsSwitchingDealer] = useState(false);

  const ensureTemporaryDealerIsPersisted = async expectedDealerId => {
    for (let attemptIndex = 0; attemptIndex < 5; attemptIndex++) {
      const { data: profile } = await axios.get(`${HOST}/dealer/brp-club/authentication/account/profile`);
      if (isSameDealerId(resolveTemporaryDealerId(profile), expectedDealerId)) {
        return;
      }

      if (attemptIndex < 4) {
        await wait(200);
      }
    }

    throw new Error('temporaryDealerNotPersisted');
  };

  const copyArtifactsToSelectedDealer = async () => {
    if (!copyPayload) {
      return;
    }

    const postBrpClub = (relativeUrl, payload) => axios.post(`${HOST}${relativeUrl}`, payload);

    const sourceCartItems = copyPayload?.cart?.cartItems || [];
    const sourceCartWalls = copyPayload?.cart?.cartWalls || [];
    const sourceCartToolings = copyPayload?.cart?.cartToolings || [];
    const sourceExteriorSignageItems = copyPayload?.exteriorSignageQuote?.quoteItems || [];
    const sourceMerchandisingProgramItems = copyPayload?.merchandisingProgramQuote?.quoteItems || [];

    for (const cartItem of sourceCartItems) {
      await postBrpClub('/api/dealer-portal/brp-club/cart/items', {
        id: uuidv4(),
        quantity: cartItem.quantity,
        sku: cartItem.sku,
        name: cartItem.name,
        quantityPerPackage: cartItem.quantityPerPackage,
        unitPrice: cartItem.unitPrice,
        unitPricePromotion: cartItem.unitPricePromotion,
        subTotal: cartItem.subTotal,
        currencyName: cartItem.currencyName,
        isDemo: Boolean(cartItem.isDemo),
        imgSrc: cartItem.imgSrc,
        inventoryItem_Id: cartItem.inventoryItem_Id,
        inventoryItemCategoryEnum: cartItem.inventoryItemCategoryEnum,
        inventoryItemVariant_Id: cartItem.inventoryItemVariant_Id,
        inventoriedQuantityAvailable: cartItem.inventoriedQuantityAvailable,
        isInventoriedItemQuantityExceeded: Boolean(cartItem.isInventoriedItemQuantityExceeded),
        isInventoriedItemDepleted: Boolean(cartItem.isInventoriedItemDepleted),
        inventoryItemOnceDepleted_Id: cartItem.inventoryItemOnceDepleted_Id,
        inventoryItemOnceDepletedName: cartItem.inventoryItemOnceDepletedName,
        inventoryItemOnceDepletedSku: cartItem.inventoryItemOnceDepletedSku,
        promotionQuantity: cartItem.promotionQuantity,
        bundledItems: [],
      });
    }

    for (const cartWall of sourceCartWalls) {
      await postBrpClub('/api/dealer-portal/brp-club/cart/walls', {
        id: uuidv4(),
        dateCreated: new Date(),
        numberOfPanels: cartWall.numberOfPanels,
        hasShadowBox: Boolean(cartWall.hasShadowBox),
        quantity: cartWall.quantity,
        name: cartWall.name,
        description: cartWall.description,
        shortDescription: cartWall.shortDescription,
        subTotal: cartWall.subTotal,
        price: cartWall.price,
        currencyName: cartWall.currencyName,
        isDemo: Boolean(cartWall.isDemo),
        panelHeader_Id: cartWall.panelHeader_Id,
        panelType_Id: cartWall.panelType_Id,
        images: cartWall.images || [],
        cartWallInventoryItems: (cartWall.cartWallInventoryItems || []).map(wallItem => ({
          id: uuidv4(),
          dateCreated: new Date(),
          quantity: wallItem.quantity,
          displayOnly: Boolean(wallItem.displayOnly),
          name: wallItem.name,
          inventoryItem_Id: wallItem.inventoryItem_Id,
        })),
      });
    }

    for (const cartTooling of sourceCartToolings) {
      await postBrpClub('/api/dealer-portal/brp-club/cart/toolings', {
        id: uuidv4(),
        dateCreated: new Date(),
        quantity: cartTooling.quantity,
        tooling_Id: cartTooling.tooling_Id,
        name: cartTooling.name,
        isDemo: Boolean(cartTooling.isDemo),
        imgSrc: cartTooling.imgSrc,
        subTotal: cartTooling.subTotal,
        price: cartTooling.price,
        currencyName: cartTooling.currencyName,
        additionalToolings: (cartTooling.additionalToolings || []).map(additionalTooling => ({
          id: uuidv4(),
          dateCreated: new Date(),
          name: additionalTooling.name,
          quantity: additionalTooling.quantity,
          tooling_Id: additionalTooling.tooling_Id,
        })),
      });
    }

    for (const exteriorSignageItem of sourceExteriorSignageItems) {
      await postBrpClub('/api/dealer-portal/brp-club/exterior-signage-quotes/items', {
        id: uuidv4(),
        dateCreated: new Date(),
        quantity: exteriorSignageItem.quantity,
        sku: exteriorSignageItem.sku,
        name: exteriorSignageItem.name,
        unitPrice: exteriorSignageItem.unitPrice,
        subTotal: exteriorSignageItem.subTotal,
        isDemo: Boolean(exteriorSignageItem.isDemo),
        currencyName: exteriorSignageItem.currencyName,
        allowOEM: Boolean(exteriorSignageItem.allowOEM),
        quantityOEM: exteriorSignageItem.quantityOEM,
        allowDealerLogo: Boolean(exteriorSignageItem.allowDealerLogo),
        imgSrc: exteriorSignageItem.imgSrc,
        exteriorSignageCategory_Id: exteriorSignageItem.exteriorSignageCategory_Id,
        exteriorSignage_Id: exteriorSignageItem.exteriorSignage_Id,
      });
    }

    for (const merchandisingProgramItem of sourceMerchandisingProgramItems) {
      await postBrpClub('/api/dealer-portal/brp-club/merchandising-program-quotes/items', {
        id: uuidv4(),
        dateCreated: new Date(),
        name: merchandisingProgramItem.name,
        visitLength: merchandisingProgramItem.visitLength,
        merchandisingProgramVisitLengthCategoryEnum: merchandisingProgramItem.merchandisingProgramVisitLengthCategoryEnum,
        quantity: merchandisingProgramItem.quantity,
        unitPrice: merchandisingProgramItem.unitPrice,
        subTotal: merchandisingProgramItem.subTotal,
        currencyName: merchandisingProgramItem.currencyName,
        merchandisingProgramCategory_Id: merchandisingProgramItem.merchandisingProgramCategory_Id,
        merchandisingProgramQuoteItemSubCategoryEnum: merchandisingProgramItem.merchandisingProgramQuoteItemSubCategoryEnum,
        merchandisingProgramSeasonCategoryEnum: merchandisingProgramItem.merchandisingProgramSeasonCategoryEnum,
        isDemo: Boolean(merchandisingProgramItem.isDemo),
        imgSrc: merchandisingProgramItem.imgSrc,
        merchandisingProgram_Id: merchandisingProgramItem.merchandisingProgram_Id,
      });
    }
  };

  const normalizedAvailableDealers = useMemo(() => {
    if (Array.isArray(availableDealers)) {
      return availableDealers;
    }

    if (Array.isArray(availableDealers?.items)) {
      return availableDealers.items;
    }

    if (Array.isArray(availableDealers?.data)) {
      return availableDealers.data;
    }

    return [];
  }, [availableDealers]);

  const dealerOptions = useMemo(
    () =>
      normalizedAvailableDealers.map(dealer => ({
        id: dealer.id,
        isRelatedToCurrentUser: dealer.isRelatedToCurrentUser === true,
        languageEnum: dealer.language,
        name: `${dealer.identifier || ''} - ${dealer.customerName || ''}`,
      })),
    [normalizedAvailableDealers],
  );

  const shouldGroupDealers = useMemo(() => {
    const hasRelatedDealers = dealerOptions.some(dealer => dealer.isRelatedToCurrentUser);
    const hasOtherDealers = dealerOptions.some(dealer => !dealer.isRelatedToCurrentUser);
    return hasRelatedDealers && hasOtherDealers;
  }, [dealerOptions]);

  const selectedDealer = dealerOptions.find(dealer => dealer.id === selectedDealerId) || null;

  const getDealerLanguageCode = dealerLanguageEnum => (dealerLanguageEnum === LanguageEnumValue.FRENCH ? 'fr' : 'en');

  const applyLanguage = languageCode => {
    localStorage.setItem(localeStorageLanguageKey, languageCode);
    i18n.changeLanguage(languageCode);
    if (languageCode === 'fr') moment.locale('fr-CA');
    else moment.locale('en-CA');
  };

  useEffect(() => {
    if (!show) {
      setSelectedDealerId(null);
      setDealerSearchInput('');
      setHasSavedDealerSelection(false);
      return;
    }

    const currentLanguageCode = i18n.language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    setInitialLanguageCode(currentLanguageCode);
  }, [show, i18n.language]);

  const handleModalClose = () => {
    if (!hasSavedDealerSelection) {
      applyLanguage(initialLanguageCode);
    }
    onClose();
  };

  const handleEmployeeDealerSelection = async () => {
    if (!selectedDealer?.id || isSwitchingDealer) {
      return;
    }

    setIsSwitchingDealer(true);

    const currentDealerId = accountReducer?.profile?.temporaryDealer_Id || accountReducer?.profile?.dealer_Id;
    const hasDealerChanged = selectedDealer.id !== currentDealerId;

    try {
      if (IsClub && hasDealerChanged && switchMode !== 'cartConfirmation') {
        await Promise.allSettled([
          activeCart?.id ? deleteCart({ id: activeCart.id }) : Promise.resolve(),
          activeExteriorSignageQuote?.id
            ? deleteExteriorSignageQuote({ id: activeExteriorSignageQuote.id })
            : Promise.resolve(),
          activeMerchandisingProgramQuote?.id
            ? deleteMerchandisingProgramQuote({ id: activeMerchandisingProgramQuote.id })
            : Promise.resolve(),
        ]);
      }

      const dealerLanguageEnum =
        selectedDealer.languageEnum === LanguageEnumValue.FRENCH ? LanguageEnumValue.FRENCH : LanguageEnumValue.ENGLISH;
      const dealerLanguageCode = getDealerLanguageCode(dealerLanguageEnum);

      const patchedProfile = await dispatch(
        brpClubpatchProfile({
          fieldValues: [
            {
              value: dealerLanguageEnum,
              fieldName: 'person.languageEnum',
            },
            {
              value: selectedDealer.id,
              fieldName: 'temporaryDealer_Id',
            },
          ],
        }),
      );

      if (!isSameDealerId(resolveTemporaryDealerId(patchedProfile), selectedDealer.id)) {
        await ensureTemporaryDealerIsPersisted(selectedDealer.id);
      }

      applyLanguage(dealerLanguageCode);

      dispatch(
        updateProfile({
          ...(accountReducer?.profile || {}),
          person: {
            ...(accountReducer?.profile?.person || {}),
            languageEnum: dealerLanguageEnum,
          },
          dealer_Id: selectedDealer.id,
          temporaryDealer_Id: selectedDealer.id,
        }),
      );

      if (IsClub && hasDealerChanged && switchMode === 'cartConfirmation') {
        await copyArtifactsToSelectedDealer();
      }

      const refreshedCartResponse = await axios.get(
        `${HOST}/api/dealer-portal/cart/active`,
        IsImpersonationMode ? { params: { dealerId: selectedDealer.id } } : undefined,
      );
      queryClient.setQueryData(['cart'], refreshedCartResponse.data);

      setHasSavedDealerSelection(true);
      queryClient.removeQueries({ queryKey: ['dealer'] });
      queryClient.removeQueries({ queryKey: ['dealerPortalSectionLandingPage'] });
      queryClient.invalidateQueries({ queryKey: ['carts'] });
      onClose();
    } catch {
      errorMessage({
        message: 'error',
      });
    } finally {
      setIsSwitchingDealer(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <Dialog
      open={show}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '1px solid #2f2f2f',
          boxShadow: '0 14px 32px rgba(0, 0, 0, 0.28)',
          overflow: 'hidden',
        },
      }}>
      <DialogTitle
        sx={{
          backgroundColor: '#ffffff',
          color: '#111111',
          fontWeight: 700,
          fontSize: '1.15rem',
          pb: 1.25,
          borderBottom: '1px solid #ececec',
        }}>
        {t('employee_select_dealer_title')}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', backgroundColor: '#ffffff' }}>
        <Box mb={2} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid #f0e1a0', backgroundColor: '#fff9e6' }}>
          <Typography variant="body2" sx={{ color: '#1f1f1f' }}>
            {t('dealer-select')}
          </Typography>
        </Box>
        <CustomAutocomplete
          labelText={t('employee_select_dealer_label')}
          hasSkeleton
          isLoading={isLoadingDealers}
          options={dealerOptions}
          formControlProps={{
            fullWidth: true,
          }}
          autocompleteProps={{
            onChange: (event, value) => {
              const nextSelectedDealerId = value?.id || null;
              if (nextSelectedDealerId !== selectedDealerId) {
                setSelectedDealerId(nextSelectedDealerId);
              }
            },
            inputValue: dealerSearchInput,
            onInputChange: (event, inputValue) => {
              setDealerSearchInput(inputValue || '');
            },
            isOptionEqualToValue: (dealer, value) => dealer.id === value?.id,
            value: selectedDealer,
            getOptionLabel: dealer => dealer?.name || '',
            groupBy: shouldGroupDealers
              ? dealer =>
                  dealer.isRelatedToCurrentUser
                    ? t('employee_select_dealer_group_yours')
                    : t('employee_select_dealer_group_other')
              : undefined,
            renderGroup: params => (
              <li key={params.key}>
                <Box px={1.5} py={0.5}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    {params.group}
                  </Typography>
                </Box>
                <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
              </li>
            ),
            filterOptions: (dealers, state) => {
              var searchTerms = state.inputValue.toLowerCase().split(' ');
              return dealers.filter(dealer =>
                searchTerms.every(searchTerm => dealer.name?.toLowerCase().includes(searchTerm)),
              );
            },
          }}
        />
        {dealerOptions?.length === 0 && !isLoadingDealers && (
          <Box mt={2}>
            <Typography variant="body1">{t('employee_select_no_associated_dealers')}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #ececec', backgroundColor: '#fafafa' }}>
        <Button
          onClick={handleEmployeeDealerSelection}
          variant="contained"
          sx={{
            backgroundColor: '#FFD200',
            color: '#111111',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#e8be00' },
          }}
          disabled={!selectedDealerId || isLoadingDealers || isSwitchingDealer}>
          {t('button.save')}
        </Button>
        <Button
          onClick={handleModalClose}
          sx={{
            color: '#111111',
            borderColor: '#c9c9c9',
            '&:hover': { borderColor: '#111111', backgroundColor: '#f4f4f4' },
          }}
          variant="outlined">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickDealerChangeModal;
